import React, { useCallback, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import { formatNumber, VALIDATABLE_DIE_SET_PREFIXES } from '@ztechapps/common.utils'
import type { Product } from '@ztechapps/common.contract'
import { ProductTypeEnum } from '@ztechapps/common.contract'
import { Stack } from 'react-bootstrap'
import { Alert, Box, HorizontalStack, Icon, ShowIf } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { TextInput } from '@ztechapps/webui.input'
import { NumberInput } from '@ztechapps/webui.input'
import { useAiChatContext } from '../ai-chat/Context'
import type { ProductDraft, ProductProposal } from './ProductProposal'
import { ProductProposalResolutionSourceEnum } from './ProductProposal'

// Mirrors the `dieSetType` strings emitted by the backend's
// `parseProductCode`. Validation rejects anything else inline so the admin
// sees the problem before submit. The shared constant lives in
// `@ztechapps/common.utils` (`product/dieSetPrefixes.ts`) so the backend
// MCP tool and the WebUI card stay in lock-step.
const CODE_PREFIX_REGEX = new RegExp(`^(${VALIDATABLE_DIE_SET_PREFIXES.join('|')})-`)

const SHAPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'square', label: 'Square (DD)' },
  { value: 'rectangle', label: 'Rectangle (DD)' },
  { value: 'rounded', label: 'Rounded (RD)' },
  { value: 'single-d', label: 'Single-D (SDD)' },
  { value: 'double-d', label: 'Double-D (DD)' },
  { value: 'special', label: 'Special (SPL)' },
]

export type CreateProductProposalCardProps = {
  /** The full proposal payload decoded from the MCP resource. */
  proposal: ProductProposal
  /** Parent-side commit callback. Resolves on success, rejects on failure. */
  onCreate: () => Promise<void>
  /** Bubble field edits to the parent so the patch survives re-renders. */
  onEdit: (patch: Partial<ProductDraft>) => void
  /** True while the parent has a commit in flight for THIS card. */
  busy: boolean
  /** Terminal state after commit. Drives the success/failure badge. */
  result?: 'created' | 'failed'
  /** Error message rendered next to the failure badge when `result === 'failed'`. */
  error?: string
  /**
   * The resolved-after-commit product. When set, the card shows a
   * "View product" link to the newly-created entity (mirrors the
   * existing-duplicate "View product" link).
   */
  createdProduct?: { _id: string; code: string }
}

export const CreateProductProposalCard = ({
  proposal,
  onCreate,
  onEdit,
  busy,
  result,
  error,
  createdProduct,
}: CreateProductProposalCardProps) => {
  const { productUrl } = useAiChatContext()

  const initialDraft = proposal.draft
  const [code, setCode] = useState(initialDraft.code ?? '')
  const [name, setName] = useState(initialDraft.name ?? '')
  const [shape, setShape] = useState(initialDraft.shape ?? '')
  const [verticalIn, setVerticalIn] = useState<number | undefined>(initialDraft.verticalIn)
  const [horizontalIn, setHorizontalIn] = useState<number | undefined>(initialDraft.horizontalIn)
  const [cornerRadiusIn, setCornerRadiusIn] = useState<number | undefined>(initialDraft.cornerRadiusIn)
  const [price, setPrice] = useState<number | undefined>(initialDraft.price)
  const [baseProductCode, setBaseProductCode] = useState(initialDraft.baseProductCode ?? '')

  const isVendorMode = initialDraft.productType === ProductTypeEnum.Vendor
  const isLocked = result === 'created'

  const validation = validateDraft({
    code,
    isVendorMode,
    verticalIn,
    horizontalIn,
    cornerRadiusIn,
  })

  const handleEdit = useCallback(
    (patch: Partial<ProductDraft>) => {
      onEdit(patch)
    },
    [onEdit]
  )

  const handleCodeChange = (next: string) => {
    setCode(next)
    handleEdit({ code: next })
  }

  const handleNameChange = (next: string) => {
    setName(next)
    handleEdit({ name: next })
  }

  const handleShapeChange = (next: string) => {
    setShape(next)
    handleEdit({ shape: next })
  }

  const handleNumberChange =
    (setter: (value: number | undefined) => void, key: 'verticalIn' | 'horizontalIn' | 'cornerRadiusIn' | 'price') =>
    (raw: string) => {
      if (raw === '' || raw === undefined) {
        setter(undefined)
        handleEdit({ [key]: undefined })
        return
      }
      const parsed = toNumber(raw)
      if (!Number.isFinite(parsed)) {
        setter(undefined)
        handleEdit({ [key]: undefined })
        return
      }
      setter(parsed)
      handleEdit({ [key]: parsed })
    }

  const handleBaseCodeChange = (next: string) => {
    setBaseProductCode(next)
    handleEdit({ baseProductCode: next })
  }

  const handleCreate = useCallback(() => {
    if (!validation.canSubmit) return Promise.resolve()
    return onCreate()
  }, [onCreate, validation.canSubmit])

  const titleLabel = buildTitle({ verticalIn, horizontalIn, shape, name })

  const duplicate = proposal.duplicate
  const showDuplicateView = !!duplicate && !isLocked
  const duplicateUrl = duplicate && productUrl ? productUrl(duplicateAsProduct(duplicate)) : undefined
  const createdUrl = createdProduct && productUrl ? productUrl({ _id: createdProduct._id } as Product) : undefined

  return (
    <Box className="bg-body">
      <Stack gap={2}>
        <HorizontalStack gap={2} className="justify-content-between align-items-start">
          <div className="fw-semibold text-body text-truncate flex-grow-1">{titleLabel}</div>

          <ShowIf condition={result === 'created'}>
            <span className="badge bg-success-subtle text-success-emphasis flex-shrink-0">Created</span>
          </ShowIf>
          <ShowIf condition={result === 'failed'}>
            <span className="badge bg-danger-subtle text-danger-emphasis flex-shrink-0">Failed</span>
          </ShowIf>
        </HorizontalStack>

        <ShowIf condition={showDuplicateView}>
          <DuplicateWarning duplicate={duplicate} url={duplicateUrl} />
        </ShowIf>

        <ShowIf condition={!isLocked}>
          <Stack gap={2}>
            <FieldRow label="Code">
              <TextInput
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                disabled={busy}
                autoFocus={false}
              />
            </FieldRow>

            <FieldRow label="Name">
              <TextInput
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                disabled={busy}
                autoFocus={false}
              />
            </FieldRow>

            <ShowIf condition={!isVendorMode}>
              <HorizontalStack gap={2} className="align-items-end">
                <div className="flex-grow-1">
                  <FieldRow label="Shape">
                    <ShapeSelect value={shape} disabled={busy} onChange={handleShapeChange} />
                  </FieldRow>
                </div>
                <div className="flex-grow-1">
                  <FieldRow label="Corner R (in)">
                    <NumberInput
                      value={cornerRadiusIn ?? ''}
                      onChange={(e) => handleNumberChange(setCornerRadiusIn, 'cornerRadiusIn')(e.target.value)}
                      disabled={busy}
                      min={0}
                      step={0.01}
                    />
                  </FieldRow>
                </div>
              </HorizontalStack>

              <HorizontalStack gap={2}>
                <div className="flex-grow-1">
                  <FieldRow label="Vertical (in)">
                    <NumberInput
                      value={verticalIn ?? ''}
                      onChange={(e) => handleNumberChange(setVerticalIn, 'verticalIn')(e.target.value)}
                      disabled={busy}
                      min={0}
                      step={0.01}
                    />
                  </FieldRow>
                </div>
                <div className="flex-grow-1">
                  <FieldRow label="Horizontal (in)">
                    <NumberInput
                      value={horizontalIn ?? ''}
                      onChange={(e) => handleNumberChange(setHorizontalIn, 'horizontalIn')(e.target.value)}
                      disabled={busy}
                      min={0}
                      step={0.01}
                    />
                  </FieldRow>
                </div>
              </HorizontalStack>
            </ShowIf>

            <FieldRow label="Price (USD)">
              <NumberInput
                value={price ?? ''}
                onChange={(e) => handleNumberChange(setPrice, 'price')(e.target.value)}
                disabled={busy}
                min={0}
                step={0.01}
              />
            </FieldRow>

            <ShowIf condition={!isVendorMode}>
              <FieldRow label="Clone from (code)">
                <TextInput
                  value={baseProductCode}
                  onChange={(e) => handleBaseCodeChange(e.target.value)}
                  disabled={busy}
                  placeholder="e.g. DD-15001500"
                  autoFocus={false}
                />
              </FieldRow>
            </ShowIf>
          </Stack>
        </ShowIf>

        <ShowIf condition={!isEmpty(validation.errors)}>
          <Alert type="warning">
            <ul className="mb-0 ps-3">
              {validation.errors.map((msg) => (
                <li key={msg} className="text-body small">
                  {msg}
                </li>
              ))}
            </ul>
          </Alert>
        </ShowIf>

        <FootnoteLine
          isVendorMode={isVendorMode}
          baseProductCode={baseProductCode}
          source={proposal.resolution.source}
          distance={proposal.resolution.distance}
          warning={proposal.resolution.warning}
          vendors={initialDraft.vendors}
        />

        <ShowIf condition={result === 'failed' && !!error}>
          <Alert type="error">
            <span className="text-body small">{error}</span>
          </Alert>
        </ShowIf>

        <HorizontalStack gap={2} className="justify-content-end align-items-center">
          <ShowIf condition={!!createdUrl}>
            <a
              href={createdUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
              aria-label={`View product ${createdProduct?.code ?? ''}`}
            >
              <BusyButton variant="outline-primary" bsSize="sm" text="View product" />
            </a>
          </ShowIf>

          <ShowIf condition={showDuplicateView && !!duplicateUrl}>
            <a
              href={duplicateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
              aria-label={`View product ${duplicate?.code ?? ''}`}
            >
              <BusyButton variant="outline-primary" bsSize="sm" text="View product" />
            </a>
          </ShowIf>

          <ShowIf condition={!isLocked && !showDuplicateView}>
            <BusyButton
              variant="primary"
              bsSize="sm"
              text={result === 'failed' ? 'Retry' : 'Create'}
              isBusy={busy}
              busyText="Creating…"
              disabled={!validation.canSubmit}
              onClick={handleCreate}
            />
          </ShowIf>
        </HorizontalStack>
      </Stack>
    </Box>
  )
}

type FieldRowProps = { label: string; children: React.ReactNode }
const FieldRow = ({ label, children }: FieldRowProps) => (
  <Stack gap={1}>
    <span className="small text-body-secondary">{label}</span>
    {children}
  </Stack>
)

type ShapeSelectProps = {
  value: string
  disabled?: boolean
  onChange: (next: string) => void
}
const ShapeSelect = ({ value, disabled, onChange }: ShapeSelectProps) => (
  <select
    className="form-select form-select-sm"
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">Select a shape…</option>
    {SHAPE_OPTIONS.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </select>
)

type DuplicateWarningProps = {
  duplicate?: { _id: string; code: string; name?: string; coverImage?: string }
  url?: string
}
const DuplicateWarning = ({ duplicate }: DuplicateWarningProps) => {
  if (!duplicate) return null
  return (
    <Alert type="warning">
      <HorizontalStack gap={2} className="align-items-center">
        <Icon name="exclamation-triangle" className="text-warning-emphasis" />
        <span className="text-body small">
          <span className="fw-semibold">{duplicate.code}</span> already exists in the catalog
          {duplicate.name ? ` — ${duplicate.name}` : ''}.
        </span>
      </HorizontalStack>
    </Alert>
  )
}

type FootnoteLineProps = {
  isVendorMode: boolean
  baseProductCode: string
  source: ProductProposal['resolution']['source']
  distance?: number
  warning?: string
  vendors?: ProductProposal['draft']['vendors']
}
const FootnoteLine = ({ isVendorMode, baseProductCode, source, distance, warning, vendors }: FootnoteLineProps) => {
  if (isVendorMode) {
    const vendorId = vendors?.[0]?.vendorId
    const vendorCode = vendors?.[0]?.vendorCode
    return (
      <div className="small text-body-secondary">
        From PO line — vendor {vendorId ?? 'unknown'}
        {vendorCode ? ` (SKU ${vendorCode})` : ''}.
      </div>
    )
  }

  if (source === ProductProposalResolutionSourceEnum.None) {
    return (
      <div className="small text-warning-emphasis">
        {warning ?? 'No close catalog match — pick a clone base manually.'}
      </div>
    )
  }

  if (isEmpty(baseProductCode)) {
    return null
  }

  if (source === ProductProposalResolutionSourceEnum.Override) {
    return (
      <div className="small text-body-secondary">
        Cloned from: <span className="fw-semibold">{baseProductCode}</span> (manual override)
      </div>
    )
  }

  if (source === ProductProposalResolutionSourceEnum.Auto) {
    return (
      <div className="small text-body-secondary">
        Cloned from: <span className="fw-semibold">{baseProductCode}</span>
        {typeof distance === 'number' ? ` (closest match — Δ ${formatNumber(distance, '0.00')}″)` : ' (closest match)'}
      </div>
    )
  }

  return null
}

type ValidateInput = {
  code: string
  isVendorMode: boolean
  verticalIn?: number
  horizontalIn?: number
  cornerRadiusIn?: number
}
const validateDraft = (input: ValidateInput): { errors: string[]; canSubmit: boolean } => {
  const errors: string[] = []

  if (isEmpty(input.code)) {
    errors.push('Code is required.')
  } else if (!CODE_PREFIX_REGEX.test(input.code)) {
    errors.push(`Code must start with one of: ${VALIDATABLE_DIE_SET_PREFIXES.join(', ')}.`)
  }

  if (!input.isVendorMode) {
    if (!Number.isFinite(input.verticalIn) || (input.verticalIn ?? 0) <= 0) {
      errors.push('Vertical must be greater than 0.')
    }
    if (!Number.isFinite(input.horizontalIn) || (input.horizontalIn ?? 0) <= 0) {
      errors.push('Horizontal must be greater than 0.')
    }
    if (typeof input.cornerRadiusIn === 'number' && input.cornerRadiusIn < 0) {
      errors.push('Corner radius cannot be negative.')
    }
  }

  return { errors, canSubmit: isEmpty(errors) }
}

const buildTitle = (params: { verticalIn?: number; horizontalIn?: number; shape?: string; name?: string }): string => {
  const { verticalIn, horizontalIn, shape, name } = params
  if (typeof verticalIn === 'number' && typeof horizontalIn === 'number') {
    const v = formatNumber(verticalIn, '0.00')
    const h = formatNumber(horizontalIn, '0.00')
    const label = shape ? labelForShape(shape) : 'Punch'
    return `${v}" × ${h}" ${label}`
  }
  return name ?? 'Proposed product'
}

const labelForShape = (shape: string): string => {
  switch (shape) {
    case 'square':
      return 'Square Punch'
    case 'rectangle':
      return 'Rectangle Punch'
    case 'rounded':
      return 'Rounded Punch'
    case 'single-d':
      return 'Single-D Punch'
    case 'double-d':
      return 'Double-D Punch'
    case 'special':
      return 'Special Punch'
    default:
      return 'Punch'
  }
}

const duplicateAsProduct = (snapshot: { _id: string; code: string; name?: string; coverImage?: string }): Product =>
  ({
    _id: snapshot._id,
    code: snapshot.code,
    name: snapshot.name,
    coverImage: snapshot.coverImage,
  }) as Product
